<template>
  <div
    class="min-h-screen flex flex-col items-center justify-center py-6 px-4 sm:px-6 lg:px-8 bg-lightGray bg-cover bg-center relative overflow-hidden"
  >
    <div
      class="max-w-xl w-full space-y-8 bg-white/80 backdrop-blur-xl p-8 rounded-2xl overflow-hidden shadow-2xl shadow-cosmicPurple/10 relative z-10"
    >
      <ShineBorder
        :border-width="2"
        :duration="24"
        :shine-color="['#A07CFE', '#FE8FB5', '#A07CFE']"
      />
      <div class="relative flex items-center justify-center">
        <div class="absolute -top-[25rem] z-10 bg-[#4d7aff] w-[30rem] h-[30rem] rounded-full blur-[100px] opacity-30 appear"></div>
        <LogoMainLink class="z-20" />
      </div>

      <!-- Loading state -->
      <div v-if="isLoading" class="flex justify-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-2 border-cosmicPurple border-t-transparent"></div>
      </div>

      <!-- Step Navigation -->
      <div v-else-if="!showSuccessMessage">
        <div class="text-center">
          <h2 class="text-2xl text-gray-900 text-pretty font-light">
            Complete Your Profile
          </h2>
          <p class="mt-2 text-sm text-gray-600">
            Please fill in the required information to continue.
          </p>
        </div>

        <!-- Step 1: Basic Information -->
        <div v-if="currentStep === 1">
          <Form @submit.prevent="nextStep" class="mt-8 space-y-6 max-w-[20rem] mx-auto">
            <div class="space-y-4">
              <FormField v-slot="{ componentField }" name="first_name">
                <FormItem>
                  <FormControl>
                    <FormLabel>First name *</FormLabel>
                    <Input
                      v-bind="componentField"
                      v-model="form.first_name"
                      placeholder="Enter your first name"
                      required
                    />
                  </FormControl>
                  <p v-if="validation_errors.first_name" class="text-red-500 text-sm">
                    {{ validation_errors.first_name }}
                  </p>
                </FormItem>
              </FormField>

              <FormField v-slot="{ componentField }" name="last_name">
                <FormItem>
                  <FormControl>
                    <FormLabel>Last name *</FormLabel>
                    <Input
                      v-bind="componentField"
                      v-model="form.last_name"
                      placeholder="Enter your last name"
                      required
                    />
                  </FormControl>
                  <p v-if="validation_errors.last_name" class="text-red-500 text-sm">
                    {{ validation_errors.last_name }}
                  </p>
                </FormItem>
              </FormField>

              <FormField v-slot="{ componentField }" name="telegram_handle">
                <FormItem>
                  <FormControl>
                    <FormLabel>Telegram username *</FormLabel>
                    <Input
                      v-bind="componentField"
                      v-model="form.telegram_handle"
                      placeholder="@username"
                      required
                    />
                  </FormControl>
                  <p v-if="validation_errors.telegram_handle" class="text-red-500 text-sm">
                    {{ validation_errors.telegram_handle }}
                  </p>
                </FormItem>
              </FormField>
            </div>

            <Button
              type="submit"
              class="w-full bg-cosmicPurple shadow-lg shadow-cosmicPurple/20 text-white hover:bg-cosmicPurple/90 hover:-translate-y-[1px] transition-all duration-200"
              @click="validateAndContinueStep1"
            >
              Continue
            </Button>
          </Form>
        </div>

        <!-- Step 2: Profile URLs -->
        <div v-if="currentStep === 2">
          <Form @submit.prevent="nextStep" class="mt-8 space-y-6 max-w-[20rem] mx-auto">
            <div class="space-y-4">
              <FormField v-slot="{ componentField }" name="linkedin_url">
                <FormItem>
                  <FormControl>
                    <FormLabel>LinkedIn profile URL *</FormLabel>
                    <Input
                      v-bind="componentField"
                      v-model="form.linkedin_url"
                      type="url"
                      placeholder="https://linkedin.com/in/yourprofile"
                      required
                    />
                  </FormControl>
                  <p v-if="validation_errors.linkedin_url" class="text-red-500 text-sm">
                    {{ validation_errors.linkedin_url }}
                  </p>
                </FormItem>
              </FormField>

              <FormField v-slot="{ componentField }" name="leetcode_url">
                <FormItem>
                  <FormControl>
                    <FormLabel>LeetCode profile URL *</FormLabel>
                    <Input
                      v-bind="componentField"
                      v-model="form.leetcode_url"
                      type="url"
                      placeholder="https://leetcode.com/yourprofile"
                      required
                    />
                  </FormControl>
                  <p v-if="validation_errors.leetcode_url" class="text-red-500 text-sm">
                    {{ validation_errors.leetcode_url }}
                  </p>
                </FormItem>
              </FormField>
            </div>

            <div class="flex flex-col gap-4">
              <Button
                type="submit"
                class="w-full bg-cosmicPurple shadow-lg shadow-cosmicPurple/20 text-white hover:bg-cosmicPurple/90 hover:-translate-y-[1px] transition-all duration-200"
                @click="validateAndContinueStep2"
              >
                Continue
              </Button>
              <button
                @click="prevStep"
                type="button"
                class="w-full text-sm bg-transparent text-gray-500 hover:text-gray-900 transition-all duration-200"
              >
                Back
              </button>
            </div>
          </Form>
        </div>

        <!-- Step 3: Additional Information -->
        <div v-if="currentStep === 3">
          <Form @submit.prevent="submitProfile" class="mt-8 space-y-6 max-w-[20rem] mx-auto">
            <div class="space-y-4">
              <FormField name="country">
                <FormItem>
                  <FormLabel>Country of residence *</FormLabel>
                  <FormControl>
                    <Select v-model="form.country_of_residence">
                      <SelectTrigger>
                        <SelectValue placeholder="Select a country" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          v-for="country in countries"
                          :key="country.id"
                          :value="country.id"
                        >
                          {{ country.country_name }}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <p v-if="validation_errors.country_of_residence" class="text-red-500 text-sm">
                    {{ validation_errors.country_of_residence }}
                  </p>
                </FormItem>
              </FormField>

              <FormField name="gender">
                <FormItem>
                  <FormLabel>Gender *</FormLabel>
                  <FormControl>
                    <Select v-model="form.gender">
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem v-for="gender in genders" :key="gender.id" :value="gender.id">
                          {{ gender.gender_name }}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <p v-if="validation_errors.gender" class="text-red-500 text-sm">
                    {{ validation_errors.gender }}
                  </p>
                </FormItem>
              </FormField>

              <FormField name="current_job_role">
                <FormItem>
                  <FormLabel>Current job role *</FormLabel>
                  <FormControl>
                    <Select v-model="form.current_job_role">
                      <SelectTrigger>
                        <SelectValue placeholder="Select job role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem v-for="role in jobRoles" :key="role.id" :value="role.id">
                          {{ role.job_name }}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <p v-if="validation_errors.current_job_role" class="text-red-500 text-sm">
                    {{ validation_errors.current_job_role }}
                  </p>
                </FormItem>
              </FormField>

              <FormField name="bachelor_degree">
                <FormItem>
                  <FormLabel>Do you have a Bachelor's Degree? *</FormLabel>
                  <FormControl>
                    <Select v-model="form.bachelor_degree">
                      <SelectTrigger>
                        <SelectValue placeholder="Select bachelor degree status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          v-for="option in bachelorDegreeOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <p v-if="validation_errors.bachelor_degree" class="text-red-500 text-sm">
                    {{ validation_errors.bachelor_degree }}
                  </p>
                </FormItem>
              </FormField>
            </div>

            <!-- Error message -->
            <div v-if="errorMessage" class="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p class="text-red-600 text-sm">{{ errorMessage }}</p>
            </div>

            <div class="flex gap-4 flex-col">
              <Button
                type="button"
                :disabled="submitting"
                class="w-full bg-cosmicPurple shadow-lg shadow-cosmicPurple/20 text-white hover:bg-cosmicPurple/90 hover:-translate-y-[1px] transition-all duration-200 disabled:opacity-50"
                @click="submitProfile"
              >
                {{ submitting ? 'Saving...' : 'Complete Profile' }}
              </Button>
              <button
                @click="prevStep"
                type="button"
                class="w-full text-sm bg-transparent text-gray-500 hover:text-gray-900 transition-all duration-200"
              >
                Back
              </button>
            </div>
          </Form>
        </div>

        <TermsAndConditionsLinks />
      </div>

      <!-- Success Message -->
      <div v-else class="space-y-4 text-center pb-8">
        <div class="relative mx-auto w-12 h-12">
          <Badge class="absolute top-0 left-0 w-12 h-12 text-lime-500 mx-auto spin-in" />
          <Check class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 text-lime-500" />
        </div>
        <h3 class="text-xl font-semibold text-gray-900">Profile Complete!</h3>
        <p class="text-gray-600">
          Your profile has been completed successfully. Redirecting...
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { Badge, Check } from 'lucide-vue-next'
import { API_ROUTES } from '@/const/apiRoutes'
import { socialAuthService } from '@/services/socialAuthService'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Form, FormField, FormItem, FormLabel, FormControl } from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import ShineBorder from '@/components/ui/ShineBorder.vue'
import TermsAndConditionsLinks from '@/components/common/TermsAndConditionsLinks.vue'
import LogoMainLink from '@/components/common/LogoMainLink.vue'
import type { ProfileCompletionData } from '@/types/auth'

interface BachelorDegreeOption {
  value: string
  name: string
  label: string
}

interface Gender {
  id: number
  gender_name: string
}

interface JobRole {
  id: number
  job_name: string
}

interface Country {
  id: number
  country_name: string
}

const router = useRouter()

const isLoading = ref(true)
const submitting = ref(false)
const errorMessage = ref('')
const validation_errors = ref<Record<string, string>>({})
const currentStep = ref(1)
const showSuccessMessage = ref(false)

// Dropdown options
const countries = ref<Country[]>([])
const genders = ref<Gender[]>([])
const jobRoles = ref<JobRole[]>([])
const bachelorDegreeOptions = ref<BachelorDegreeOption[]>([])

// Form data
const form = ref<ProfileCompletionData>({
  first_name: '',
  last_name: '',
  telegram_handle: '',
  linkedin_url: '',
  leetcode_url: '',
  country_of_residence: 0,
  gender: 0,
  current_job_role: 0,
  bachelor_degree: ''
})

onMounted(async () => {
  try {
    // Fetch dropdown options and profile status in parallel
    await Promise.all([
      fetchDropdownData(),
      fetchProfileStatus()
    ])
  } catch (error) {
    console.error('Failed to load profile completion page:', error)
    errorMessage.value = 'Failed to load page data. Please refresh and try again.'
  } finally {
    isLoading.value = false
  }
})

const fetchDropdownData = async () => {
  await Promise.all([
    fetchBachelorDegreeOptions(),
    fetchGenders(),
    fetchJobRoles(),
    fetchCountries()
  ])
}

const fetchBachelorDegreeOptions = async () => {
  try {
    const response = await axios.get(API_ROUTES.GET_BACHELORS_DEGREE_OPTIONS)
    bachelorDegreeOptions.value = Array.isArray(response.data)
      ? response.data
      : Object.entries(response.data).map(([key, value]) => ({ value: key, name: value as string, label: value as string }))
  } catch (error) {
    console.error('Error fetching bachelor degree options:', error)
    // Fallback options if API fails
    bachelorDegreeOptions.value = [
      { value: 'yes', name: 'Yes', label: 'Yes' },
      { value: 'no', name: 'No', label: 'No' },
      { value: 'in_progress', name: 'In Progress', label: 'In Progress' }
    ]
  }
}

const fetchGenders = async () => {
  try {
    const response = await axios.get(API_ROUTES.GET_GENDERS)
    genders.value = response.data.map((item: Gender) => ({
      id: item.id,
      gender_name: item.gender_name
    }))
  } catch (error) {
    console.error('Error fetching genders:', error)
  }
}

const fetchJobRoles = async () => {
  try {
    const response = await axios.get(API_ROUTES.GET_USER_JOB_ROLES)
    jobRoles.value = response.data.map((item: JobRole) => ({
      id: item.id,
      job_name: item.job_name
    }))
  } catch (error) {
    console.error('Error fetching job roles:', error)
  }
}

const fetchCountries = async () => {
  try {
    const response = await axios.get(API_ROUTES.GET_COUNTRIES)
    countries.value = response.data.map((item: Country) => ({
      id: item.id,
      country_name: item.country_name
    }))
  } catch (error) {
    console.error('Error fetching countries:', error)
  }
}

const fetchProfileStatus = async () => {
  try {
    const status = await socialAuthService.getProfileStatus()
    console.log('[CompleteProfile] Profile status:', status)
    
    // Pre-fill form with any existing data from social auth
    if (status.prefilled_data) {
      if (status.prefilled_data.first_name) {
        form.value.first_name = status.prefilled_data.first_name
      }
      if (status.prefilled_data.last_name) {
        form.value.last_name = status.prefilled_data.last_name
      }
      if (status.prefilled_data.linkedin_url) {
        form.value.linkedin_url = status.prefilled_data.linkedin_url
      }
      if (status.prefilled_data.telegram_handle) {
        form.value.telegram_handle = status.prefilled_data.telegram_handle
      }
      if (status.prefilled_data.leetcode_url) {
        form.value.leetcode_url = status.prefilled_data.leetcode_url
      }
      if (status.prefilled_data.country_of_residence) {
        form.value.country_of_residence = status.prefilled_data.country_of_residence
      }
      if (status.prefilled_data.gender) {
        form.value.gender = status.prefilled_data.gender
      }
      if (status.prefilled_data.current_job_role) {
        form.value.current_job_role = status.prefilled_data.current_job_role
      }
      if (status.prefilled_data.bachelor_degree) {
        // API returns uppercase (e.g., "NO", "YES", "IN_PROGRESS"), convert to lowercase for form
        form.value.bachelor_degree = status.prefilled_data.bachelor_degree.toLowerCase()
      }
    }
    
    // If profile is already complete, redirect to dashboard
    if (status.is_profile_complete) {
      console.log('[CompleteProfile] Profile already complete, redirecting...')
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.push('/')
    }
  } catch (error) {
    console.error('Failed to fetch profile status:', error)
    // Continue anyway - user can fill in the form
  }
}

const isValidUrl = (string: string): boolean => {
  try {
    new URL(string)
    return true
  } catch {
    return false
  }
}

const validateAndContinueStep1 = () => {
  validation_errors.value = {}
  let hasErrors = false

  if (!form.value.first_name?.trim()) {
    validation_errors.value.first_name = 'First name is required'
    hasErrors = true
  }
  
  if (!form.value.last_name?.trim()) {
    validation_errors.value.last_name = 'Last name is required'
    hasErrors = true
  }
  
  if (!form.value.telegram_handle?.trim()) {
    validation_errors.value.telegram_handle = 'Telegram username is required'
    hasErrors = true
  }

  if (!hasErrors) {
    currentStep.value++
  }
}

const validateAndContinueStep2 = () => {
  validation_errors.value = {}
  let hasErrors = false

  if (!form.value.linkedin_url?.trim()) {
    validation_errors.value.linkedin_url = 'LinkedIn profile URL is required'
    hasErrors = true
  } else if (!isValidUrl(form.value.linkedin_url)) {
    validation_errors.value.linkedin_url = 'Please enter a valid LinkedIn URL'
    hasErrors = true
  }
  
  if (!form.value.leetcode_url?.trim()) {
    validation_errors.value.leetcode_url = 'LeetCode profile URL is required'
    hasErrors = true
  } else if (!isValidUrl(form.value.leetcode_url)) {
    validation_errors.value.leetcode_url = 'Please enter a valid LeetCode URL'
    hasErrors = true
  }

  if (!hasErrors) {
    currentStep.value++
  }
}

const validateStep3 = (): boolean => {
  validation_errors.value = {}
  
  if (!form.value.country_of_residence) {
    validation_errors.value.country_of_residence = 'Country is required'
  }
  
  if (!form.value.gender) {
    validation_errors.value.gender = 'Gender is required'
  }
  
  if (!form.value.current_job_role) {
    validation_errors.value.current_job_role = 'Job role is required'
  }
  
  if (!form.value.bachelor_degree) {
    validation_errors.value.bachelor_degree = 'Please select your degree status'
  }
  
  return Object.keys(validation_errors.value).length === 0
}

const nextStep = () => {
  if (currentStep.value === 1) {
    validateAndContinueStep1()
  } else if (currentStep.value === 2) {
    validateAndContinueStep2()
  }
}

const prevStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--
    validation_errors.value = {}
  }
}

const submitProfile = async () => {
  if (!validateStep3()) {
    return
  }
  
  submitting.value = true
  errorMessage.value = ''
  
  try {
    // Map old BachelorDegreeStatusEnum values to the DegreeStatusChoices values the complete-profile endpoint expects
    const degreeStatusMap: Record<string, string> = {
      YES: 'YES',
      GETTING: 'YES_INCOMPLETE',
      NO: 'NO',
      QUIT: 'NO',
    }
    const rawDegree = form.value.bachelor_degree.toUpperCase()
    const submitData = {
      ...form.value,
      bachelor_degree: degreeStatusMap[rawDegree] ?? rawDegree
    }
    console.log('[CompleteProfile] Submitting profile data:', submitData)
    
    await socialAuthService.completeProfile(submitData)
    
    // Show success message
    showSuccessMessage.value = true
    
    // Redirect after a short delay
    setTimeout(() => {
      const redirectTo = localStorage.getItem('redirectAfterLogin')
      if (redirectTo) {
        localStorage.removeItem('redirectAfterLogin')
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.push(redirectTo)
      } else {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.push('/')
      }
    }, 1500)
  } catch (error: any) {
    console.error('Failed to complete profile:', error)
    errorMessage.value = error.message || 'Failed to complete profile. Please try again.'
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.appear {
  animation: appear 1.5s ease-in-out;
}

@keyframes appear {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  100% {
    opacity: 0.3;
    transform: scale(1);
  }
}

.spin-in {
  animation: spinIn 1s forwards ease-in-out;
}

@keyframes spinIn {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(180deg);
  }
}
</style>
